import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Search, Check, MapPin, TrendingUp, Info } from "lucide-react";

import PageHero from "@/components/PageHero";
import { FadeUp } from "@/components/Motion";
import SeoHead from "@/components/SeoHead";
import { useSite } from "@/contexts/SiteContext";
import { publicApi, ApiError } from "@/lib/api";

/**
 * Instant property valuation.
 *
 * Three steps: find the address from a postal code, confirm the property
 * details, then supply contact details to see the estimate.
 *
 * The contact step is not decoration. Each valuation is billed to PIRAC by the
 * upstream provider, so an ungated form would be a button that spends money for
 * an anonymous visitor. Gating it means every pound spent produces a lead in the
 * enquiry inbox — and the estimate is genuinely worth the exchange.
 *
 * The API key lives server-side; both calls here go through our own API.
 */

const HDB_FLAT_TYPES = ["2 ROOM", "3 ROOM", "4 ROOM", "5 ROOM", "EXECUTIVE", "MULTI-GENERATION"];

const SGD = (n) =>
  typeof n === "number" ? `S$${Math.round(n).toLocaleString("en-SG")}` : "—";

const CONFIDENCE_STYLE = {
  high: "text-[#4ade80] border-[#4ade80]/40",
  medium: "text-[#cca24d] border-[#cca24d]/40",
  low: "text-[#fbbf24] border-[#fbbf24]/40",
};

/** Capture attribution once, before any client-side navigation. */
function useAttribution() {
  const ref = useRef(null);
  if (ref.current === null) {
    const params = new URLSearchParams(window.location.search);
    const campaign = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"]
      .forEach((k) => { const v = params.get(k); if (v) campaign[k] = v; });
    ref.current = {
      landingPage: window.location.pathname + window.location.search,
      referralSource: document.referrer || "",
      campaignParams: campaign,
    };
  }
  return ref.current;
}

export default function PropertyValuation() {
  const { site, forms } = useSite();
  const attribution = useAttribution();

  const [postalCode, setPostalCode] = useState("");
  const [resolved, setResolved] = useState(null);
  const [details, setDetails] = useState({});
  const [lead, setLead] = useState({ fullName: "", email: "", mobile: "", consent: false });
  const [honeypot, setHoneypot] = useState("");

  const [looking, setLooking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);

  const isCondo = resolved && resolved.propertyType === "condo";

  const lookup = async (event) => {
    event.preventDefault();
    setErrors({});
    if (!/^\d{6}$/.test(postalCode.trim())) {
      setErrors({ postalCode: "Enter a 6-digit Singapore postal code." });
      return;
    }

    setLooking(true);
    setResult(null);
    try {
      let found = await publicApi.resolvePostalCode({ postalCode: postalCode.trim() });

      /*
        The upstream only returns a floor-area suggestion when it knows which
        flat/condo type to suggest for. For HDB that arrives when the visitor
        picks a flat type; for a condo the first response already tells us the
        condo_type, so a second lookup fills the field straight away rather than
        leaving private-property owners to guess a number HDB owners get free.
      */
      if (found.propertyType === "condo" && found.condoType && !found.areaSqmSuggestion) {
        try {
          const refined = await publicApi.resolvePostalCode({
            postalCode: postalCode.trim(),
            condoType: found.condoType,
          });
          if (refined.areaSqmSuggestion) found = refined;
        } catch (_) {
          // No suggestion is a smaller problem than no address; keep going.
        }
      }

      setResolved(found);
      // Pre-fill everything the lookup already knows, so the visitor confirms
      // rather than transcribes.
      setDetails({
        flatType: "",
        floorAreaSqm: found.floorAreaSuggestion ? String(found.floorAreaSuggestion.sqm ?? "") : "",
        storeyRange: (found.storeyRangeOptions || [])[0] || "",
        areaSqm: found.areaSqmSuggestion ? String(found.areaSqmSuggestion.sqm ?? "") : "",
        floorRange: (found.floorRangeOptions || [])[0] || "",
        unitNumber: "",
      });
    } catch (err) {
      setResolved(null);
      if (err instanceof ApiError && err.fieldErrors) setErrors(err.fieldErrors);
      else toast.error(err.message || "We could not find that postal code.");
    } finally {
      setLooking(false);
    }
  };

  /** Re-run the lookup once a flat type is known — it returns a floor-area suggestion. */
  const refineForFlatType = async (flatType) => {
    setDetails((d) => ({ ...d, flatType }));
    if (!flatType || isCondo) return;
    try {
      const found = await publicApi.resolvePostalCode({ postalCode: postalCode.trim(), flatType });
      if (found.floorAreaSuggestion && found.floorAreaSuggestion.sqm) {
        setDetails((d) => ({ ...d, floorAreaSqm: String(found.floorAreaSuggestion.sqm) }));
      }
      if ((found.storeyRangeOptions || []).length) {
        setResolved((r) => ({ ...r, storeyRangeOptions: found.storeyRangeOptions }));
      }
    } catch (_) {
      // A failed refinement just means no suggestion; the field stays editable.
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    const next = {};
    if (!lead.fullName.trim()) next.fullName = "Full name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email)) next.email = "Enter a valid email address.";
    if (!/^\+?[0-9\s\-()]{7,}$/.test(lead.mobile)) next.mobile = "Enter a valid mobile number.";
    if (!lead.consent) next.consent = "Please confirm you agree to be contacted.";
    if (!isCondo && !details.flatType) next.flatType = "Select the flat type.";
    if (!isCondo && !details.floorAreaSqm) next.floorAreaSqm = "Enter the floor area.";
    if (isCondo && !details.areaSqm) next.areaSqm = "Enter the floor area.";
    setErrors(next);
    if (Object.keys(next).length) {
      toast.error("Please correct the highlighted fields.");
      return;
    }

    setSubmitting(true);
    try {
      const property = isCondo
        ? {
            propertyType: "condo",
            postalCode: resolved.postalCode,
            project: resolved.project,
            district: resolved.district,
            condoType: resolved.condoType,
            tenureType: resolved.tenureType,
            leaseYears: resolved.leaseYears,
            leaseCommenceYear: resolved.leaseCommenceYear,
            areaSqm: details.areaSqm,
            floorRange: details.floorRange,
            unitNumber: details.unitNumber,
          }
        : {
            propertyType: "hdb",
            postalCode: resolved.postalCode,
            town: resolved.town,
            block: resolved.block,
            streetName: resolved.streetName,
            leaseCommenceDate: resolved.leaseCommenceDate,
            flatType: details.flatType,
            floorAreaSqm: details.floorAreaSqm,
            storeyRange: details.storeyRange,
            unitNumber: details.unitNumber,
          };

      const response = await publicApi.requestValuation({
        ...lead,
        property,
        ...attribution,
        [forms.honeypotField]: honeypot,
      });

      setResult(response.valuation);
      toast.success("Your estimate is ready.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      if (err instanceof ApiError && err.fieldErrors) {
        setErrors(err.fieldErrors);
        toast.error(err.message);
      } else if (err instanceof ApiError && err.isRateLimited) {
        toast.error(err.message, { duration: 8000 });
      } else {
        toast.error(err.message || "We could not complete that valuation. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setResult(null);
    setResolved(null);
    setPostalCode("");
    setDetails({});
    setErrors({});
  };

  return (
    <>
      <SeoHead pageKey="property_valuation" />

      <PageHero
        eyebrow="Property Valuation"
        chapter="Chapter — Tools"
        lines={["What is your", "property worth?"]}
        blurb="An instant, evidence-based estimate for Singapore HDB flats and private condominiums — built from recent comparable transactions, not a guess."
        image="/assets/valuation-factors"
        imageFit="contain"
        imageAlt="The ten factors behind a property valuation: location, age and condition, appearance and amenities, size and furnishings, builder reputation, recent sales of similar properties, market demand and supply, future development, economic factors, and legal and title status"
      />

      <section className="bg-[#060f1e] py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-8">
          {/* ------------------------------------------------------- Result */}
          {result ? (
            <div className="col-span-12 lg:col-span-8">
              <FadeUp>
                <div className="border border-[#cca24d]/50 bg-[#0c1728] p-8 lg:p-12" data-testid="valuation-result">
                  <div className="overline text-[#cca24d]/80">Estimated market value</div>
                  <div className="mt-4 font-serif text-5xl md:text-6xl lg:text-7xl text-[#f7f5f0] tnum leading-none">
                    {SGD(result.estimatedValue)}
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
                    <span className="text-[#9ca3af]">
                      {result.estimatedPsf ? `${SGD(result.estimatedPsf)} psf` : ""}
                    </span>
                    {result.confidence && (
                      <span
                        className={`px-3 py-1 border text-xs uppercase tracking-widest ${
                          CONFIDENCE_STYLE[result.confidence] || CONFIDENCE_STYLE.medium
                        }`}
                      >
                        {result.confidence} confidence
                      </span>
                    )}
                  </div>

                  {result.confidenceReason && (
                    <p className="mt-6 text-sm text-[#d8d8d8] leading-relaxed">
                      {result.confidenceReason}
                      {result.lookbackMonths ? ` Comparables drawn from the last ${result.lookbackMonths} months.` : ""}
                    </p>
                  )}

                  {result.remainingLeaseMonths ? (
                    <p className="mt-3 text-sm text-[#9ca3af]">
                      Remaining lease at valuation:{" "}
                      <span className="text-[#f7f5f0] tnum">
                        {Math.floor(result.remainingLeaseMonths / 12)} years {result.remainingLeaseMonths % 12} months
                      </span>
                    </p>
                  ) : null}

                  <div className="mt-10 pt-8 border-t border-[#1a2940] flex flex-wrap gap-4">
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-3 px-7 h-13 py-4 bg-[#cca24d] text-[#060f1e] text-sm font-medium hover:bg-[#e5c98b] transition-colors"
                    >
                      Discuss this with an advisor
                      <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                    </Link>
                    <button
                      type="button"
                      onClick={reset}
                      className="inline-flex items-center gap-2 px-7 py-4 border border-[#1a2940] text-[#d8d8d8] text-sm hover:border-[#cca24d] hover:text-[#cca24d] transition-colors"
                    >
                      Value another property
                    </button>
                  </div>
                </div>
              </FadeUp>

              {/* Comparables — the evidence behind the number. */}
              {result.comparables && result.comparables.length > 0 && (
                <FadeUp delay={0.1}>
                  <div className="mt-8 border border-[#1a2940] bg-[#0c1728]">
                    <div className="p-6 lg:p-8 border-b border-[#1a2940]">
                      <div className="overline">The evidence</div>
                      <h2 className="mt-3 font-serif text-2xl text-[#f7f5f0]">
                        {result.comparableCount} comparable transaction{result.comparableCount === 1 ? "" : "s"}
                      </h2>
                      <p className="mt-2 text-sm text-[#9ca3af]">
                        The most-weighted {result.comparables.length} are shown. Each is adjusted for time,
                        storey and size before contributing to the estimate.
                      </p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm min-w-[640px]">
                        <thead>
                          <tr className="text-left text-[10px] uppercase tracking-widest text-[#9ca3af] border-b border-[#1a2940]">
                            <th className="px-6 py-4 font-medium">Month</th>
                            <th className="px-6 py-4 font-medium">Address</th>
                            <th className="px-6 py-4 font-medium">Storey</th>
                            <th className="px-6 py-4 font-medium text-right">Area</th>
                            <th className="px-6 py-4 font-medium text-right">Price</th>
                            <th className="px-6 py-4 font-medium text-right">Adj. psf</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#1a2940]">
                          {result.comparables.map((c, i) => (
                            <tr key={`${c.address}-${c.month}-${i}`} className="text-[#d8d8d8]">
                              <td className="px-6 py-4 tnum whitespace-nowrap">{c.month || "—"}</td>
                              <td className="px-6 py-4">
                                <div>{c.address || "—"}</div>
                                {(c.street || c.saleType) && (
                                  <div className="text-xs text-[#6b7280] mt-1">
                                    {[c.street, c.saleType].filter(Boolean).join(" · ")}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 tnum whitespace-nowrap">{c.storeyRange || "—"}</td>
                              <td className="px-6 py-4 tnum text-right whitespace-nowrap">
                                {c.floorAreaSqm ? `${c.floorAreaSqm} sqm` : "—"}
                              </td>
                              <td className="px-6 py-4 tnum text-right whitespace-nowrap">{SGD(c.price)}</td>
                              <td className="px-6 py-4 tnum text-right whitespace-nowrap text-[#cca24d]">
                                {c.adjustedPsf ? SGD(c.adjustedPsf) : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </FadeUp>
              )}

              <FadeUp delay={0.15}>
                <div className="mt-8 p-6 border border-[#1a2940] bg-[#0c1728] flex gap-4">
                  <Info className="h-4 w-4 text-[#cca24d] shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div className="text-xs text-[#9ca3af] leading-relaxed space-y-2">
                    <p>
                      This is an automated estimate derived from comparable transactions. It does not
                      account for renovation, orientation, view, unit-specific condition or current
                      market sentiment, and it is not a formal valuation for financing, legal or tax
                      purposes. Speak to a PIRAC advisor before relying on it for a decision.
                    </p>
                    <p>{result.attribution}</p>
                  </div>
                </div>
              </FadeUp>
            </div>
          ) : (
            /* --------------------------------------------------------- Form */
            <div className="col-span-12 lg:col-span-8">
              {/* Step 1 — postal code */}
              <FadeUp>
                <form onSubmit={lookup} className="border border-[#1a2940] bg-[#0c1728] p-8" data-testid="valuation-lookup">
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 flex items-center justify-center border border-[#cca24d] text-[#cca24d] text-xs tnum">1</span>
                    <span className="overline">Find your property</span>
                  </div>
                  <label className="block mt-6">
                    <span className="overline block mb-2 text-[#9ca3af]">Postal code</span>
                    <div className="flex gap-3">
                      <input
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        inputMode="numeric"
                        autoComplete="postal-code"
                        placeholder="e.g. 560216"
                        data-testid="valuation-postal"
                        className="val-input flex-1"
                      />
                      <button
                        type="submit"
                        disabled={looking}
                        data-testid="valuation-lookup-submit"
                        className="inline-flex items-center gap-2 px-6 bg-[#cca24d] text-[#060f1e] text-sm font-medium hover:bg-[#e5c98b] transition-colors disabled:opacity-60"
                      >
                        {looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" strokeWidth={1.5} />}
                        Find
                      </button>
                    </div>
                    {errors.postalCode && (
                      <span className="block text-[#ef4444] text-xs mt-2">{errors.postalCode}</span>
                    )}
                  </label>
                </form>
              </FadeUp>

              {/* Step 2 + 3 */}
              {resolved && (
                <FadeUp delay={0.05}>
                  <form onSubmit={submit} className="mt-6 border border-[#1a2940] bg-[#0c1728] p-8 space-y-8">
                    <div
                      aria-hidden="true"
                      style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}
                    >
                      <label htmlFor={`v-${forms.honeypotField}`}>Do not fill this in</label>
                      <input
                        id={`v-${forms.honeypotField}`}
                        name={forms.honeypotField}
                        tabIndex={-1}
                        autoComplete="off"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                      />
                    </div>

                    <div>
                      <div className="flex items-center gap-3">
                        <span className="w-7 h-7 flex items-center justify-center border border-[#cca24d] text-[#cca24d] text-xs tnum">2</span>
                        <span className="overline">Confirm the details</span>
                      </div>

                      <div className="mt-5 flex items-start gap-3 p-4 border border-[#cca24d]/30 bg-[#060f1e]">
                        <MapPin className="h-4 w-4 text-[#cca24d] shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="text-sm">
                          <div className="text-[#f7f5f0]">
                            {isCondo
                              ? resolved.project || "Private residential"
                              : [resolved.block, resolved.streetName].filter(Boolean).join(" ")}
                          </div>
                          <div className="text-[#9ca3af] text-xs mt-1">
                            {isCondo
                              ? [resolved.district && `District ${resolved.district}`, resolved.marketSegment, resolved.tenureType]
                                  .filter(Boolean).join(" · ")
                              : [resolved.town, resolved.leaseCommenceDate && `Lease from ${resolved.leaseCommenceDate}`]
                                  .filter(Boolean).join(" · ")}
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 grid md:grid-cols-2 gap-6">
                        {!isCondo && (
                          <Field label="Flat type" err={errors.flatType} required>
                            <select
                              value={details.flatType || ""}
                              onChange={(e) => refineForFlatType(e.target.value)}
                              data-testid="valuation-flat-type"
                              className="val-input"
                            >
                              <option value="">Select…</option>
                              {HDB_FLAT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </Field>
                        )}

                        <Field
                          label="Floor area (sqm)"
                          err={isCondo ? errors.areaSqm : errors.floorAreaSqm}
                          required
                        >
                          <input
                            type="number"
                            min="20"
                            max="1000"
                            value={isCondo ? details.areaSqm || "" : details.floorAreaSqm || ""}
                            onChange={(e) =>
                              setDetails((d) => ({ ...d, [isCondo ? "areaSqm" : "floorAreaSqm"]: e.target.value }))
                            }
                            data-testid="valuation-area"
                            className="val-input"
                          />
                        </Field>

                        {!isCondo && (resolved.storeyRangeOptions || []).length > 0 && (
                          <Field label="Storey">
                            <select
                              value={details.storeyRange || ""}
                              onChange={(e) => setDetails((d) => ({ ...d, storeyRange: e.target.value }))}
                              className="val-input"
                            >
                              {resolved.storeyRangeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </Field>
                        )}

                        {isCondo && (resolved.floorRangeOptions || []).length > 0 && (
                          <Field label="Floor range">
                            <select
                              value={details.floorRange || ""}
                              onChange={(e) => setDetails((d) => ({ ...d, floorRange: e.target.value }))}
                              className="val-input"
                            >
                              {resolved.floorRangeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </Field>
                        )}

                        <Field label="Unit number (optional)">
                          <input
                            value={details.unitNumber || ""}
                            onChange={(e) => setDetails((d) => ({ ...d, unitNumber: e.target.value }))}
                            placeholder="#12-03"
                            className="val-input"
                          />
                        </Field>
                      </div>
                    </div>

                    <div className="pt-8 border-t border-[#1a2940]">
                      <div className="flex items-center gap-3">
                        <span className="w-7 h-7 flex items-center justify-center border border-[#cca24d] text-[#cca24d] text-xs tnum">3</span>
                        <span className="overline">Where should we send it?</span>
                      </div>
                      <p className="mt-4 text-sm text-[#9ca3af] max-w-xl leading-relaxed">
                        Your estimate appears on screen straight away. We ask for your details so an
                        advisor can follow up if you would like the numbers explained.
                      </p>

                      <div className="mt-6 space-y-6">
                        <Field label="Name" err={errors.fullName} required>
                          <input
                            value={lead.fullName}
                            onChange={(e) => setLead({ ...lead, fullName: e.target.value })}
                            autoComplete="name"
                            data-testid="valuation-name"
                            className="val-input"
                          />
                        </Field>
                        <div className="grid md:grid-cols-2 gap-6">
                          <Field label="Mobile" err={errors.mobile} required>
                            <input
                              value={lead.mobile}
                              onChange={(e) => setLead({ ...lead, mobile: e.target.value })}
                              autoComplete="tel"
                              inputMode="tel"
                              placeholder="+65 "
                              data-testid="valuation-mobile"
                              className="val-input"
                            />
                          </Field>
                          <Field label="Email" err={errors.email} required>
                            <input
                              type="email"
                              value={lead.email}
                              onChange={(e) => setLead({ ...lead, email: e.target.value })}
                              autoComplete="email"
                              data-testid="valuation-email"
                              className="val-input"
                            />
                          </Field>
                        </div>

                        <div className="flex items-start gap-3">
                          <input
                            id="val-consent"
                            type="checkbox"
                            checked={lead.consent}
                            onChange={(e) => setLead({ ...lead, consent: e.target.checked })}
                            data-testid="valuation-consent"
                            className="mt-1 h-4 w-4 accent-[#cca24d]"
                          />
                          <label htmlFor="val-consent" className="text-sm text-[#d8d8d8] leading-relaxed">
                            I consent to PIRAC contacting me about this valuation, and I have read the{" "}
                            <Link to="/privacy-policy" className="text-[#cca24d] editorial-link">privacy policy</Link>.
                            {errors.consent && (
                              <span className="block text-[#ef4444] text-xs mt-1">{errors.consent}</span>
                            )}
                          </label>
                        </div>

                        <button
                          type="submit"
                          disabled={submitting}
                          data-testid="valuation-submit"
                          className="group inline-flex items-center gap-3 px-8 h-14 bg-[#cca24d] text-[#060f1e] text-sm font-medium tracking-wide hover:bg-[#e5c98b] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {submitting ? (
                            <><Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Valuing</>
                          ) : (
                            <><TrendingUp className="h-4 w-4" strokeWidth={1.5} /> Show my estimate</>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </FadeUp>
              )}
            </div>
          )}

          {/* ------------------------------------------------------- Sidebar */}
          <aside className="col-span-12 lg:col-span-4">
            <FadeUp delay={0.1}>
              <div className="border border-[#1a2940] bg-[#0c1728] p-8 space-y-6">
                <div>
                  <div className="overline mb-3">How this works</div>
                  <ul className="space-y-3 text-sm text-[#9ca3af] leading-relaxed">
                    {[
                      "Recent transactions for comparable properties are selected — same area, similar size, similar storey.",
                      "Each is adjusted for when it sold, the storey it was on and its floor area.",
                      "The adjusted figures are weighted to produce a single estimate, with a confidence label based on how closely they agree.",
                    ].map((line) => (
                      <li key={line} className="flex gap-3">
                        <Check className="h-3.5 w-3.5 text-[#cca24d] shrink-0 mt-1" strokeWidth={2} />
                        {line}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-6 border-t border-[#1a2940]">
                  <div className="overline mb-3">Covered</div>
                  <p className="text-sm text-[#9ca3af] leading-relaxed">
                    HDB resale flats and private condominiums across Singapore. Landed property is
                    assessed individually — <Link to="/contact" className="text-[#cca24d] editorial-link">speak to an advisor</Link>.
                  </p>
                </div>

                <div className="pt-6 border-t border-[#1a2940]">
                  <div className="overline mb-3">Prefer to talk?</div>
                  <p className="text-sm text-[#9ca3af] leading-relaxed mb-4">
                    An automated estimate is a starting point, not a strategy.
                  </p>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-sm text-[#cca24d] editorial-link"
                  >
                    Book a consultation <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </Link>
                  {site.contactPhone && (
                    <a
                      href={`tel:${site.contactPhone.replace(/\s/g, "")}`}
                      className="mt-3 min-h-11 py-2 inline-flex items-center text-sm text-[#d8d8d8] hover:text-[#cca24d]"
                    >
                      {site.contactPhone}
                    </a>
                  )}
                </div>
              </div>
            </FadeUp>
          </aside>
        </div>
      </section>

      <style>{`
        .val-input {
          width: 100%;
          background: #060f1e;
          border: 1px solid #1a2940;
          color: #f7f5f0;
          padding: 0.85rem 1rem;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.95rem;
          border-radius: 2px;
          transition: border-color 200ms ease, box-shadow 200ms ease;
        }
        .val-input:focus { outline: none; border-color: #cca24d; box-shadow: 0 0 0 3px rgba(204,162,77,0.15); }
      `}</style>
    </>
  );
}

function Field({ label, err, required, children }) {
  return (
    <label className="block">
      <span className="overline block mb-2 text-[#9ca3af]">
        {label}
        {required && <span className="text-[#ef4444] ml-1">*</span>}
      </span>
      {children}
      {err && <span className="block text-[#ef4444] text-xs mt-1">{err}</span>}
    </label>
  );
}
